import {
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    HStack,
    Icon,
    TextStyle,
    VStack,
    Box,
    Button,
    Text,
    Drawer,
} from '@cad-arc';
import { chunk } from 'lodash';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { CommandLineEditorStore } from './command-line-editor-store';
import { ConfigTokenName, ConfigTokens, useConfigTokenDisplayName } from './helpers';

export const CommandLineDebug = observer(function CommandLineDebug(props: {
    editorStore: CommandLineEditorStore;
}): React.ReactElement {
    const getConfigTokenDisplayName = useConfigTokenDisplayName();
    const { editorStore } = props;

    return (
        <Drawer
            variant="overlay"
            isOpen={true}
            size="lg"
            onClose={() => editorStore.toggleDebugOpen()}
        >
            <DrawerContent>
                <DrawerHeader>Command Line Debug</DrawerHeader>
                <DrawerBody>
                    <div>
                        <Text style={{ textAlign: 'center' }} variant="headingXs">
                            PARSING CONFIGURATION
                        </Text>

                        <HStack alignItems="flex-start">
                            {chunk(Object.keys(editorStore.config.tokens), 6).map((chunk, idx) => {
                                return (
                                    <table key={idx} style={{ width: '50%' }}>
                                        <thead>
                                            <th>
                                                <Text
                                                    variant="bodyMd"
                                                    style={{
                                                        paddingRight: 'var(--arc-space-4)',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    <TextStyle>Token</TextStyle>
                                                </Text>
                                            </th>
                                            <th>
                                                <Text
                                                    variant="bodyMd"
                                                    style={{ textAlign: 'left' }}
                                                >
                                                    <TextStyle>Name</TextStyle>
                                                </Text>
                                            </th>
                                        </thead>
                                        <tbody>
                                            {chunk.map((key, idx) => {
                                                return (
                                                    <tr key={idx}>
                                                        <td
                                                            style={{
                                                                paddingRight: 'var(--arc-space-4)',
                                                                textAlign: 'right',
                                                            }}
                                                            width="50%"
                                                        >
                                                            <Text variant="bodyMd">
                                                                <TextStyle fontWeight="bold">
                                                                    {
                                                                        (editorStore.config
                                                                            .tokens as ConfigTokens)[
                                                                            key as keyof ConfigTokens
                                                                        ]
                                                                    }
                                                                </TextStyle>
                                                            </Text>
                                                            {}
                                                        </td>
                                                        <td>
                                                            <Text variant="bodyMd">
                                                                <TextStyle fontWeight="normal">
                                                                    {getConfigTokenDisplayName(
                                                                        key as ConfigTokenName
                                                                    )}
                                                                </TextStyle>
                                                            </Text>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                );
                            })}
                        </HStack>

                        <Text style={{ marginTop: 'var(--arc-space-8)' }} variant="headingXs">
                            ACTIVE COMMAND
                        </Text>
                        {editorStore.activeCommandSegment ? (
                            <Box>
                                <Text variant="bodyMd">
                                    {editorStore.activeCommandSegment.segmentData.title} (
                                    {
                                        editorStore.activeCommandSegment.segmentData
                                            .argumentDefinition
                                    }
                                    )
                                </Text>
                                <Text variant="bodyMd">
                                    {editorStore.activeCommandSegment.segmentData.description}
                                </Text>
                                <Text variant="bodyMd">
                                    (e.g:{' '}
                                    {
                                        editorStore.config.helpText.find(
                                            (h) =>
                                                h.cliArgumentConfigurationView
                                                    .argumentDefinition ===
                                                editorStore.activeCommandSegment?.segmentData
                                                    .argumentDefinition
                                        )?.exampleText
                                    }
                                    )
                                </Text>
                            </Box>
                        ) : (
                            <Text>N/A</Text>
                        )}

                        <Box mt="8">
                            <Text variant="headingXs">CURRENT SEGMENTS</Text>
                            <VStack mt="4" alignItems="left">
                                {editorStore.currentSegments.length ? (
                                    editorStore.currentSegments.map((segment, idx) => {
                                        return (
                                            <Box key={idx}>
                                                <HStack>
                                                    <Box
                                                        style={{
                                                            background:
                                                                'var(--arc-colors-brand-active)',
                                                        }}
                                                    >
                                                        {segment.segmentType}
                                                    </Box>
                                                    {editorStore.entityForSegment(segment) ? (
                                                        <Icon
                                                            style={{
                                                                marginLeft: 'var(--arc-space-2)',
                                                            }}
                                                            size="sm"
                                                            icon="SupervisorApproved"
                                                            color="positive.active"
                                                        />
                                                    ) : null}
                                                    <Text variant="caption">
                                                        From: {segment.start} to{' '}
                                                        {segment.start + segment.length}
                                                    </Text>
                                                </HStack>
                                                <HStack>
                                                    <Text variant="caption">
                                                        <>
                                                            DATA:{' '}
                                                            {segment.segmentType === 'COMMAND'
                                                                ? segment.segmentData.cliArgument
                                                                : segment.segmentData}
                                                        </>
                                                    </Text>
                                                    Entity:{' '}
                                                    {
                                                        editorStore.entityForSegment(segment)
                                                            ?.segmentText
                                                    }
                                                </HStack>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Text>N/A</Text>
                                )}
                            </VStack>
                        </Box>

                        {editorStore.helperState ? (
                            <Box mt="8">
                                <Text variant="headingXs">ACTIVE HELPER</Text>
                                <Box
                                    style={{
                                        background: 'var(--arc-colors-warning-active)',
                                    }}
                                >
                                    {editorStore.helperState.activeHelper}
                                </Box>
                                <Text variant="caption">Query: {editorStore.helperSearchText}</Text>
                            </Box>
                        ) : null}

                        <Box mt="8">
                            <Text variant="headingXs">EXECUTION VIEW</Text>
                            <Box mt="2" bg="surface.canvas">
                                <pre>
                                    <Text style={{ fontFamily: 'roboto-mono' }} variant="caption">
                                        {JSON.stringify(editorStore.executionView, null, 2)}
                                    </Text>
                                </pre>
                            </Box>
                        </Box>
                    </div>
                </DrawerBody>
                <DrawerFooter>
                    <Box as="span" mr={3}>
                        <Button variant="outline" onClick={() => editorStore.toggleDebugOpen()}>
                            Close
                        </Button>
                    </Box>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
});
